import { useState } from "react";

import { DetailDrawer, Pair } from "../../../components/admin/DetailDrawer";
import type { AdminJiraLogEntry } from "../../../lib/api";

const COLUMNS = [
  "When",
  "Actor",
  "Source",
  "Slug",
  "Kind",
  "Issues",
  "ms",
  "Outcome",
  "Cached",
  "",
];

/** jira_log rows as a table; each row's JQL button opens its detail drawer. */
export function JiraQueryResults({ items }: { items: AdminJiraLogEntry[] }) {
  const [drawer, setDrawer] = useState<AdminJiraLogEntry | null>(null);
  return (
    <>
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-slate-500">
          <tr>
            {COLUMNS.map((h) => (
              <th key={h} className="py-2 pr-2">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <tr key={e.id} className="border-t border-slate-100">
              <td className="py-2 pr-2 text-slate-500">
                {new Date(e.created_at).toLocaleString()}
              </td>
              <td className="py-2 pr-2">{e.actor_email}</td>
              <td className="py-2 pr-2">{e.source}</td>
              <td className="py-2 pr-2 font-mono">
                {e.slug ? (
                  <a
                    href={`/a/${e.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {e.slug}
                  </a>
                ) : (
                  "—"
                )}
              </td>
              <td className="py-2 pr-2">{e.kind}</td>
              <td className="py-2 pr-2 text-right">
                {e.issues_returned}
                {e.truncated ? "+" : ""}
              </td>
              <td className="py-2 pr-2 text-right">{e.duration_ms}</td>
              <td
                className={`py-2 pr-2 ${e.outcome === "success" ? "text-emerald-700" : "text-rose-700"}`}
              >
                {e.outcome}
              </td>
              <td className="py-2 pr-2">{e.cached ? "yes" : "no"}</td>
              <td className="py-2 pr-2 text-right">
                <button
                  type="button"
                  onClick={() => setDrawer(e)}
                  className="rounded border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50"
                >
                  JQL
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {drawer && (
        <DetailDrawer title="Query detail" onClose={() => setDrawer(null)}>
          <dl className="space-y-1 text-sm">
            <Pair k="Outcome" v={drawer.outcome} />
            <Pair k="Actor" v={drawer.actor_email} />
            <Pair k="Source" v={drawer.source} />
            <Pair k="Slug" v={drawer.slug ?? "—"} />
            <Pair k="Kind" v={drawer.kind} />
            <Pair k="JQL hash" v={drawer.jql_hash} />
            <Pair k="Fields" v={drawer.fields.join(", ") || "—"} />
            <Pair
              k="Issues"
              v={`${drawer.issues_returned}${drawer.truncated ? " (truncated)" : ""}`}
            />
            <Pair k="Pages" v={String(drawer.pages)} />
            {drawer.error_detail && <Pair k="Error" v={drawer.error_detail} />}
          </dl>
          <h4 className="mt-4 text-sm font-semibold">
            JQL as written (first 1024 chars)
          </h4>
          <pre className="mt-1 max-h-[60vh] overflow-auto rounded bg-slate-50 p-3 text-xs">
            {drawer.jql_truncated}
          </pre>
        </DetailDrawer>
      )}
    </>
  );
}
